import { Col, Form, Row } from 'antd';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import FormSelect, { SelectOption } from 'components/new-ui/composed/formSelect/FormSelect';
import CloseButton from 'components/new-ui/composed/modal/filters-modal/CloseButton';
import FiltersModal from 'components/new-ui/composed/modal/filters-modal/FiltersModal';
import { useMemo } from 'react';
import {
    DeclarationCountry,
    declarationCountryDisplay,
    declarationExternalEntityCountryMap,
} from 'store/declarations/enums/common/declaration-country';
import { DeclarationExternalEntity } from 'store/declarations/enums/common/declaration-external-entity';
import { DeclarationStatuses } from 'store/declarations/enums/common/declaration-status';
import { DeclarationParamsKeys } from './components/useDeclarationsFilters';
import { getMessageTypeSelectOptions, MessageTypes } from 'store/declarations/enums/common/declaration-types';
import {
    DeclarationInternalType,
    getDeclarationInternalTypeSelectOptions,
} from 'store/declarations/enums/common/declaration-internal-type';
import DeclarationStatusSelect from './components/DeclarationStatusSelect';
import { useParams } from 'react-router-dom';

const countryOptions: SelectOption[] = [
    { value: declarationCountryDisplay.ireland, id: 'REVENUE' },
    { value: declarationCountryDisplay.uk, id: 'HMRC' },
];

export type ModalFilters = {
    query: string | undefined;
    declarationExternalEntity?: DeclarationExternalEntity | undefined;
    declarationStatus: DeclarationStatuses[] | undefined;
    declarationType: MessageTypes[] | undefined;
    declarationInternalType?: DeclarationInternalType[] | undefined;
};

type Props = {
    open?: boolean;
    filters: ModalFilters;
    onClose: () => void;
    onSubmit: () => void;
    onClearAll: () => void;
    onClearFilter: (key: string, value?: string) => void;
    onFiltersChange: (path: Partial<ModalFilters>) => void;
};

const DeclarationFiltersModal = ({
    open,
    filters,
    onClose,
    onSubmit,
    onClearAll,
    onClearFilter,
    onFiltersChange,
}: Props) => {
    const params = useParams();

    const { query, declarationExternalEntity, declarationStatus, declarationType, declarationInternalType } = filters;

    const country: DeclarationCountry | undefined = useMemo(() => {
        if (params.country) return params.country as DeclarationCountry;
        if (declarationExternalEntity) return declarationExternalEntityCountryMap[declarationExternalEntity];
        return undefined;
    }, [declarationExternalEntity, params.country]);

    const messageTypeOptions: SelectOption[] = useMemo(() => {
        return getMessageTypeSelectOptions(country, declarationInternalType ?? undefined);
    }, [country, declarationInternalType]);

    const declarationTypeOptions: SelectOption[] = useMemo(() => {
        if (country) {
            return getDeclarationInternalTypeSelectOptions(country);
        }

        return getDeclarationInternalTypeSelectOptions();
    }, [country]);

    return (
        <FiltersModal
            width={840}
            open={open}
            footer={null}
            header={
                <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 16 }}>
                    <SearchBar
                        style={{ width: '100%' }}
                        inputPlaceholder="Search declarations"
                        onSearch={(val) => onFiltersChange({ query: val })}
                        value={query}
                    />
                    <CloseButton onClose={onClose} />
                </div>
            }
            onClose={onClose}
            onClearFilters={onClearAll}
            onSubmit={onSubmit}
        >
            <Form layout="vertical">
                <Row gutter={40}>
                    {declarationExternalEntity && (
                        <Col span={12}>
                            <Form.Item label="Country">
                                <FormSelect
                                    hideKeys
                                    placeholder="Select"
                                    value={declarationExternalEntity}
                                    onChange={(val) => {
                                        if (val === declarationExternalEntity) return;
                                        onFiltersChange({ declarationExternalEntity: val });
                                        onClearFilter(DeclarationParamsKeys.DECLARATION_INTERNAL_TYPE);
                                        onClearFilter(DeclarationParamsKeys.DECLARATION_TYPE);
                                        onClearFilter(DeclarationParamsKeys.DECLARATION_STATUS);
                                    }}
                                    selectOptions={countryOptions}
                                    onClear={() => {
                                        onClearFilter(DeclarationParamsKeys.DECLARATION_INTERNAL_TYPE);
                                        onClearFilter(DeclarationParamsKeys.DECLARATION_EXTERNAL_ENTITY);
                                        onClearFilter(DeclarationParamsKeys.DECLARATION_TYPE);
                                        onClearFilter(DeclarationParamsKeys.DECLARATION_STATUS);
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    )}

                    {declarationInternalType && (
                        <Col span={12}>
                            <Form.Item label="Declaration Type">
                                <FormSelect
                                    hideKeys
                                    placeholder="Select"
                                    mode="multiple"
                                    value={declarationInternalType}
                                    onChange={(val) =>
                                        onFiltersChange({ declarationInternalType: val as DeclarationInternalType[] })
                                    }
                                    selectOptions={declarationTypeOptions}
                                />
                            </Form.Item>
                        </Col>
                    )}

                    <Col span={12}>
                        <Form.Item label="Form Type">
                            <FormSelect
                                hideKeys
                                mode="multiple"
                                placeholder="Select"
                                value={declarationType}
                                onChange={(val) => onFiltersChange({ declarationType: val as MessageTypes[] })}
                                selectOptions={messageTypeOptions}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="Declaration Status">
                            <DeclarationStatusSelect
                                hideKeys
                                mode="multiple"
                                placeholder="Select"
                                value={declarationStatus}
                                country={country}
                                internalType={declarationInternalType && declarationInternalType[0]}
                                onChange={(val) => onFiltersChange({ declarationStatus: val as DeclarationStatuses[] })}
                                onClear={() => onClearFilter(DeclarationParamsKeys.DECLARATION_STATUS)}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </FiltersModal>
    );
};

export default DeclarationFiltersModal;
